import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import api from 'config/axiosConfig';
import { Action } from 'util/constants';
import ActionCardRefactored from 'components/Actions/ActionCardRefactored';
import { EntityCardRef } from 'components/Entities/EntityCardBase';
import LoadingSkeleton from 'ui/LoadingSkeleton';
import { toast } from 'sonner';
import ActionProfileTabs from './ActionProfileTabs';
import NotFoundPage from 'components/ResponsePages/404NotFoundPage';
import ServerErrorPage from 'components/ResponsePages/500ServerErrorPage';
import { apiRoutes } from "util/apiRoutes";
import { applyPendingMediaOrder, syncPostCoauthors } from 'components/Posts/display/postProfileHelpers';
import { usePostProfileController } from 'components/Posts/display/usePostProfileController';
import {
  normalizeExistingCoauthors,
  usePostCoauthorState,
} from 'components/Posts/display/usePostCoauthorState';

const ActionProfile: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [description, setDescription] = useState<string>('');
  const [privacy, setPrivacy] = useState<string>('public');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const {
    entity: action,
    setEntity: setAction,
    error,
    isLoading,
    editMode,
    setEditMode,
    setSearchParams,
  } = usePostProfileController<Action>({
    id,
    fallbackKind: "action",
    getDocumentTitle: (entity) => `Action - ${entity?.title || ""}`,
    notFoundError: '404: Failed to load action profile. Please try again later.',
    genericError: 'Failed to load action profile. Please try again later.',
  });
  const actionCardRef = useRef<EntityCardRef>(null);
  const {
    coauthors,
    newCoauthors,
    removedCoauthors,
    initializeCoauthors,
    handleCoauthorsChange,
    resetCoauthorChanges,
  } = usePostCoauthorState(privacy);

  useEffect(() => {
    if (!action) {
      return;
    }

    setDescription(action.body || '');
    setPrivacy(action.properties?.privacy || 'public');
    initializeCoauthors(normalizeExistingCoauthors(action.properties?.coauthors));
  }, [action, initializeCoauthors]);

  const handleSave = async () => {
    if (isSubmitting) return;

    setIsSubmitting(true);
    try {
      const mediaOrderSuccess = await applyPendingMediaOrder(actionCardRef);
      if (!mediaOrderSuccess) {
        return;
      }

      await api.patch(apiRoutes.posts.byId(id || ""), {
        body: description,
        properties: { privacy }
      });

      await syncPostCoauthors("action", action?.id || "", newCoauthors, removedCoauthors, privacy);

      setAction(prev => prev ? {
        ...prev,
        body: description,
        properties: {
          ...prev.properties,
          privacy,
          coauthors: [...coauthors]
        }
      } : null);
      
      setEditMode(false);
      resetCoauthorChanges();
      setSearchParams({});
      
      toast.success('Action updated successfully!');
    } catch {
      toast.error('Error updating action. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (action) {
      setDescription(action.body || '');
      setPrivacy(action.properties?.privacy || 'public');
      initializeCoauthors(normalizeExistingCoauthors(action.properties?.coauthors));
    }
    setEditMode(false);
    setSearchParams({});
  };

  if (error) {
    return error.match(/404/) ? 
      <NotFoundPage 
        displayStyle="page" 
        errorMessage="Sorry, we couldn't find this action." 
        errorSubtext="It may be only visible to specific users. Double check the URL or try reloading the page." 
        buttonText="See Other Actions" 
        buttonHref="/actions" 
      /> : 
      <ServerErrorPage />;
  }

  if (isLoading || !action) {
    return <LoadingSkeleton count={1} variant="entity-card" margin="mt-4" />;
  }

  const editorButtons = editMode ? (
    <div className="editor-buttons flex justify-end gap-2">
      <button
        onClick={handleCancel}
        className="btn-secondary py-1.5"
        disabled={isSubmitting}
      >
        Cancel
      </button>
      <button
        onClick={handleSave}
        className={isSubmitting ? "btn-primary-disabled py-1.5" : "btn-primary py-1.5"}
        disabled={isSubmitting}
      >
        Save
      </button>
    </div>
  ) : null;

  return (
    <div className="py-0 ">
      <div>
        <ActionCardRefactored 
          ref={actionCardRef}
          action={{...action, properties: {...action.properties, privacy}}}
          isActionProfilePage={true}
          truncateDescription={true}
          editMode={editMode}
          onDescriptionChange={setDescription}
          onPrivacyChange={setPrivacy}
          onCoauthorsChange={handleCoauthorsChange}
        >
          {editorButtons}
        </ActionCardRefactored>
      </div>
      
      <ActionProfileTabs action={action} />
    </div>
  );
};

export default ActionProfile;
